import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import PropTypes from "prop-types";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Drawer,
  AppBar,
  List,
  CssBaseline,
  Typography,
  IconButton,
  ListItem,
  ListItemIcon,
  Toolbar
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import MoneyIcon from "@material-ui/icons/Money";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import DashboardIcon from "@material-ui/icons/Dashboard";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import AppsIcon from "@material-ui/icons/Apps";
import LibraryBooksIcon from "@material-ui/icons/LibraryBooks";
import AttachmentIcon from "@material-ui/icons/Attachment";
import SurveysIcon from "@material-ui/icons/QuestionAnswerOutlined";
import QuizIcon from "@material-ui/icons/QuestionAnswerTwoTone";
import InstituteIcon from "@material-ui/icons/School";
import AssignmentIcon from "@mui/icons-material/Assignment";
import Dropdown from "../../components/Dropdown";
import messages from "../../assets/locale/messages";
import MedblendLogo from "../../assets/images/logo.png";
import { ROUTE_PATHS } from "../../utils/PathsNames";
import Auth from "../../utils/Auth";
import { logoutRequest } from "../../store/Auth/actions";
import History from "../../routes/History";
import BooksIcon from "../../assets/svgs/BooksIcon";
import { Collapse } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MedicalFlashcardsIcon from "../../assets/svgs/MedicalFlashcardsIcon";
import TopicsListIcon from "../../assets/svgs/TopicsListIcon";
// import FlashcardIcon from "../../assets/svgs/FlashcardIcon";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex"
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    backgroundColor: theme.palette.white,
    color: theme.palette.primary.main,
    boxShadow: "none"
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  menuButton: {
    marginRight: 36
  },
  hide: {
    display: "none"
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap"
  },
  paper: {
    border: 0
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  drawerClose: {
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    overflowX: "hidden",
    width: theme.spacing(7) + 1,
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9) + 1
    }
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar
  },
  links: {
    color: theme.palette.black.light,
    textDecoration: "none",
    "&:hover": {
      color: theme.palette.white
    }
  },
  icons: {
    color: theme.palette.black.light
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(12, 3, 3, 3)
  },
  listItem: {
    padding: "20px 25px",
    fontSize: "1rem",
    fontFamily: "Helvetica-Bold",
    "&:hover, &.Mui-selected": {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.black.main,
      borderRight: `3px solid ${theme.palette.secondary.main}`
    }
  }
}));

const Layout = ({ children, lang }) => {
  const dispatch = useDispatch();
  const admin = useSelector((state) => state.auth.login);
  const [open, setOpen] = useState(false);
  const {
    finalTest,
    header: { title },
    dashboard: { title: dashboardTitle },
    programs: { title: programsTitle },
    books,
    categories,
    admins,
    authors,
    editors,
    buttons: { logout, changePassword },
    supplementaries,
    quizzes,
    surveys,
    preTestPostTest,
    promoCodes,
    institute,
    directors
  } = messages[lang];

  const classes = useStyles();
  const theme = useTheme();
  const [openFlashCard, setOpenFlashCard] = useState(true);
  const [path, setPath] = useState("");
  let location = useLocation();

  useEffect(() => {
    setPath(location.pathname);
  }, [location, setPath]);

  const activetRoute = (activeKeys) => {
    return activeKeys.some((key) => path.includes(key));
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleLogout = () => {
    dispatch(logoutRequest());
  };

  const handleChangePassword = () => {
    History.push(ROUTE_PATHS["changePassword"]);
  };

  const sideMenu = [
    {
      id: 1,
      title: dashboardTitle,
      icon: <DashboardIcon />,
      slug: ROUTE_PATHS["dashboard"],
      activeKeys: ["dashboard"]
    },
    {
      id: 2,
      title: admins.title,
      icon: <SupervisorAccountIcon />,
      slug: ROUTE_PATHS["admins"],
      activeKeys: ["admins"]
    },
    {
      id: 3,
      title: authors.title,
      icon: <SupervisorAccountIcon />,
      slug: ROUTE_PATHS["authors"],
      activeKeys: ["authors"]
    },
    {
      id: 4,
      title: editors.title,
      icon: <SupervisorAccountIcon />,
      slug: ROUTE_PATHS["editors"],
      activeKeys: ["editors"]
    },
    {
      id: 5,
      title: directors.title,
      icon: <SupervisorAccountIcon />,
      slug: ROUTE_PATHS.directors,
      activeKeys: ["directors"]
    },
    {
      id: 6,
      title: finalTest.title,
      icon: <AssignmentIcon />,
      slug: ROUTE_PATHS.finalTest,
      activeKeys: ["finalTest"]
    },
    {
      id: 7,
      title: categories.title,
      icon: <AppsIcon />,
      slug: ROUTE_PATHS["categories"],
      activeKeys: ["categories"]
    },
    {
      id: 8,
      title: books.title,
      icon: <LibraryBooksIcon />,
      slug: ROUTE_PATHS["books"],
      activeKeys: ["books", "add-book", "edit-book", "book"]
    },
    {
      id: 9,
      title: programsTitle,
      icon: <LibraryBooksIcon />,
      slug: ROUTE_PATHS.programs,
      activeKeys: ["programs"]
    },
    {
      id: 10,
      title: supplementaries.title,
      icon: <AttachmentIcon />,
      slug: ROUTE_PATHS["supplementaries"],
      activeKeys: [
        "supplementaries",
        "add-supplementary",
        "edit-supplementary",
        "supplementary"
      ]
    },
    {
      id: 11,
      title: surveys.title,
      icon: <SurveysIcon />,
      slug: ROUTE_PATHS["surveys"],
      activeKeys: ["surveys", "add-survey", "edit-survey", "survey"]
    },
    {
      id: 12,
      title: quizzes.title,
      icon: <QuizIcon />,
      slug: ROUTE_PATHS["quizzes"],
      activeKeys: ["quizzes"]
    },
    {
      id: 13,
      title: preTestPostTest.title,
      icon: <LibraryBooksIcon />,
      slug: ROUTE_PATHS["preTestPostTest"],
      activeKeys: ["tests", "add-test", "edit-test", "test"]
    },
    {
      id: 14,
      title: promoCodes.title,
      icon: <MoneyIcon />,
      slug: ROUTE_PATHS["promoCodes"],
      activeKeys: ["promoCodes", "add-promocode", "promocode"]
    },
    {
      id: 15,
      title: institute.title,
      icon: <InstituteIcon />,
      slug: ROUTE_PATHS["institute"],
      activeKeys: ["institute"]
    },
    {
      id: 16,
      title: "Packages",
      icon: <BooksIcon />,
      slug: ROUTE_PATHS.packages,
      activeKeys: ["packages"]
    }
  ];

  return Auth.getAuth() ? (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: open
        })}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            className={clsx(classes.menuButton, {
              [classes.hide]: open
            })}
          >
            <MenuIcon />
          </IconButton>
          <div className="d-flex align-items-center justify-content-between w-100">
            <Typography variant="h6" noWrap>
              {title}
            </Typography>
            <Dropdown
              title={
                admin?.name ? admin.name : admins.adminNamePlaceholder
              }
              menuItems={[
                { id: 0, title: admin?.email },
                {
                  id: 1,
                  title: changePassword,
                  handleClick: handleChangePassword
                },
                { id: 2, title: logout, handleClick: handleLogout }
              ]}
            />
          </div>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open
        })}
        classes={{
          paper: clsx(classes.paper, {
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open
          })
        }}
      >
        <div className={classes.toolbar}>
          <IconButton
            className={classes.icons}
            onClick={handleDrawerClose}
          >
            {theme.direction === "rtl" ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </div>
        <Link
          to={ROUTE_PATHS["dashboard"]}
          className="d-flex align-items-center justify-content-center mb-3"
        >
          <img
            width={`${open ? "70px" : "50px"}`}
            src={MedblendLogo}
            alt="medblend-logo"
          />
        </Link>
        <List>
          {sideMenu.map(({ id, title, icon, slug, activeKeys }) => (
            <Link className={classes.links} to={slug} key={id}>
              <ListItem
                selected={activetRoute(activeKeys)}
                className={classes.listItem}
                button
              >
                <ListItemIcon className={classes.icons}>
                  {icon}
                </ListItemIcon>
                {title}
              </ListItem>
            </Link>
          ))}

          <div style={{ display: "flex", alignItems: "center" }}>
            <Link
              className={classes.links}
              to={ROUTE_PATHS["flashcard"]}
              style={{ flex: 1 }}
            >
              <ListItem
                // selected={activetRoute(["flashcard"])}
                className={classes.listItem}
                button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpenFlashCard(!openFlashCard);
                }}
              >
                <ListItemIcon className={classes.icons}>
                  {/* Separate toggle button — does NOT navigate */}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setOpenFlashCard(!openFlashCard);
                    }}
                  >
                    {openFlashCard ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )}
                  </IconButton>
                </ListItemIcon>
                Flashcards
              </ListItem>
            </Link>
          </div>

          <Collapse in={openFlashCard} timeout="auto" unmountOnExit>
            {/* <Link
              className={classes.links}
              to={ROUTE_PATHS["flashcardDecks"]}
            >
              <ListItem className={classes.listItem} button>
                <ListItemIcon>
                  <MedicalFlashcardsIcon color="black" />
                </ListItemIcon>
                Decks
              </ListItem>
            </Link> */}
            <Link
              className={classes.links}
              to={ROUTE_PATHS["flashcardQuestions"]}
            >
              <ListItem className={classes.listItem} button>
                <ListItemIcon>
                  <MedicalFlashcardsIcon color="black" />
                </ListItemIcon>
                Questions
              </ListItem>
            </Link>
            <Link
              className={classes.links}
              to={ROUTE_PATHS["flashcardTopic"]}
            >
              <ListItem className={classes.listItem} button>
                <ListItemIcon className={classes.icons}>
                  <TopicsListIcon />
                </ListItemIcon>
                Systems
              </ListItem>
            </Link>
            {/* <Link
              className={classes.links}
              to={ROUTE_PATHS["flashcardSupTopic"]}
            >
              <ListItem
                className={classes.listItem}
                button
                style={{ paddingLeft: 40 }}
              >
                Sub Topic
              </ListItem>
            </Link> */}
          </Collapse>
        </List>
      </Drawer>
      <main className={classes.content}>{children}</main>
    </div>
  ) : (
    children
  );
};

Layout.propTypes = {
  lang: PropTypes.string,
  children: PropTypes.node
};

export default Layout;
